/**
 * The auto-drive controls: a steering wheel, a D/R shifter and a brake pedal.
 *
 * Shown instead of the steer pad and the pedals when auto-drive is on. The
 * wheel is grabbed and turned rather than tapped, so steering is analog — the
 * further you turn it, the harder the truck steers, and it self-centres the
 * moment you let go.
 *
 * The wheel only owns steering while a pointer is on it. Releasing hands the
 * axis straight back to the keyboard, so arrows and WASD keep working.
 *
 * The brake is held, not clicked, and is deliberately not the `back` action:
 * that one doubles as reverse, so holding it at a standstill would back the
 * truck up. This pedal only ever slows it down.
 */
const MAX_ANGLE = (135 * Math.PI) / 180;
const SPRING_MS = 160;

export class WheelControls {
  constructor({ input }) {
    this.input = input;

    this.root = document.getElementById('hud-auto');
    this.wheel = document.getElementById('hud-wheel');
    this.face = document.getElementById('hud-wheel-face');
    this.brake = document.getElementById('hud-brake');
    this.gearButtons = Array.from(document.querySelectorAll('[data-gear]'));

    this.enabled = false;
    this.steer = 0;
    this.dragging = false;
    this.brakeHeld = false;
    this._pointerId = null;
    this._startPointerAngle = 0;
    this._startSteer = 0;
    this._springing = false;
    this._springFrame = 0;
    this._frame = 0;

    this._onPointerDown = (event) => this.#grab(event);
    this._onPointerMove = (event) => this.#drag(event);
    this._onPointerUp = (event) => this.#release(event);
    this._onBrakeDown = (event) => this.#brakeDown(event);
    this._onBrakeUp = () => this.#brakeUp();
    this._onBrakeKeyDown = (event) => this.#brakeKeyDown(event);
    this._onBrakeKeyUp = (event) => this.#brakeKeyUp(event);

    if (this.wheel) {
      this.wheel.addEventListener('pointerdown', this._onPointerDown);
      this.wheel.addEventListener('pointermove', this._onPointerMove);
      this.wheel.addEventListener('pointerup', this._onPointerUp);
      this.wheel.addEventListener('pointercancel', this._onPointerUp);
      this.wheel.addEventListener('lostpointercapture', this._onPointerUp);
      this.wheel.addEventListener('contextmenu', (event) => event.preventDefault());
      this.wheel.addEventListener('dragstart', (event) => event.preventDefault());
    }

    if (this.brake) {
      this.brake.addEventListener('pointerdown', this._onBrakeDown);
      this.brake.addEventListener('pointerup', this._onBrakeUp);
      this.brake.addEventListener('pointercancel', this._onBrakeUp);
      this.brake.addEventListener('lostpointercapture', this._onBrakeUp);
      this.brake.addEventListener('keydown', this._onBrakeKeyDown);
      this.brake.addEventListener('keyup', this._onBrakeKeyUp);
      // Tabbing away mid-press must not leave the pedal on the floor.
      this.brake.addEventListener('blur', this._onBrakeUp);
      this.brake.addEventListener('contextmenu', (event) => event.preventDefault());
      this.brake.addEventListener('dragstart', (event) => event.preventDefault());
    }

    for (const button of this.gearButtons) {
      button.addEventListener('click', () => this.#shift(button.dataset.gear));
    }

    this.#paintGear('D');
  }

  /** Swap the HUD between the pedal layout and the wheel layout. */
  setEnabled(enabled) {
    if (this.enabled === enabled) return;
    this.enabled = enabled;

    document.getElementById('hud')?.classList.toggle('hud--auto', enabled);
    // The block is `display: none` when auto-drive is off, but an aria-hidden
    // subtree would also hide the wheel and shifter from a screen reader while
    // they are on screen and focusable.
    this.root?.setAttribute('aria-hidden', String(!enabled));

    if (enabled) {
      this.input.autoDrive = true;
      this.input.setGear('D');
      this.#paintGear('D');
      this.#startFrameLoop();
      return;
    }

    // Leaving auto-drive must not strand a held wheel, a pressed pedal or a
    // reversed gear.
    this.#brakeUp();
    this.input.autoDrive = false;
    this.input.releaseSteer();
    this.input.setGear('D');
    this.steer = 0;
    this.dragging = false;
    this._pointerId = null;
    this.#stopFrameLoop();
    this.#paint();
  }

  #grab(event) {
    if (!this.enabled || event.button !== 0 || this.dragging) return;
    event.preventDefault();
    event.stopPropagation();

    this.dragging = true;
    this._pointerId = event.pointerId;
    this._startPointerAngle = this.#pointerAngle(event);
    this._startSteer = this.steer;

    try {
      this.wheel.setPointerCapture(event.pointerId);
    } catch {
      /* the window listeners still release the drag */
    }

    this.input.setSteer(this.steer);
    this.wheel.classList.add('is-held');
  }

  #drag(event) {
    if (!this.dragging || event.pointerId !== this._pointerId) return;
    event.preventDefault();

    const delta = this.#shortestAngle(
      this.#pointerAngle(event) - this._startPointerAngle,
    );
    // Turning the wheel clockwise steers right, and right is negative.
    this.steer = clamp(this._startSteer - delta / MAX_ANGLE, -1, 1);
    this.input.setSteer(this.steer);
    this.#paint();
  }

  #release(event) {
    if (!this.dragging || event.pointerId !== this._pointerId) return;
    this.dragging = false;
    this._pointerId = null;
    this.wheel?.classList.remove('is-held');

    // Hand the axis back at once: the truck should stop steering when the
    // player lets go, not when the animation finishes.
    this.input.releaseSteer();
    this.#springBack();
  }

  /** Animate the wheel back to centre after a release. */
  #springBack() {
    const from = this.steer;
    if (Math.abs(from) < 0.001) {
      this.steer = 0;
      this.#paint();
      return;
    }

    this._springing = true;
    const start = performance.now();
    const step = () => {
      if (this.dragging) {
        this._springing = false;
        return;
      }
      const t = Math.min(1, (performance.now() - start) / SPRING_MS);
      // Ease out, so it settles rather than snapping.
      this.steer = from * (1 - t) * (1 - t);
      this.#paint();
      if (t < 1) {
        this._springFrame = requestAnimationFrame(step);
      } else {
        this.steer = 0;
        this._springing = false;
        this.#paint();
      }
    };
    cancelAnimationFrame(this._springFrame);
    this._springFrame = requestAnimationFrame(step);
  }

  /**
   * Mirror the keyboard axis onto the wheel while it is not being dragged, so
   * the control still moves when the player drives with arrows or WASD.
   */
  #startFrameLoop() {
    if (this._frame) return;
    const step = () => {
      if (!this.enabled) return;
      // The spring owns the angle while it runs; fighting it would snap the
      // wheel to centre instead of letting it settle.
      if (!this.dragging && !this._springing) {
        const axis = this.input.steerAxis;
        if (Math.abs(axis - this.steer) > 0.01) {
          this.steer = axis;
          this.#paint();
        }
      }
      this._frame = requestAnimationFrame(step);
    };
    this._frame = requestAnimationFrame(step);
  }

  #stopFrameLoop() {
    cancelAnimationFrame(this._frame);
    cancelAnimationFrame(this._springFrame);
    this._frame = 0;
    this._springFrame = 0;
  }

  #shift(gear) {
    if (!this.enabled) return;
    this.input.setGear(gear);
    this.#paintGear(gear);
  }

  #brakeDown(event) {
    if (!this.enabled) return;
    // Suppress the compatibility mouse events and the button's own activation:
    // the pedal is a hold, so there is nothing for a click to do.
    event?.preventDefault?.();
    if (this.brakeHeld) return;

    this.brakeHeld = true;
    this.input.setBrake(true);
    this.brake?.classList.add('is-held');
    this.brake?.setAttribute('aria-pressed', 'true');

    if (event?.pointerId !== undefined) {
      try {
        this.brake?.setPointerCapture(event.pointerId);
      } catch {
        /* pointerup on the button still releases it */
      }
    }
  }

  #brakeUp() {
    if (!this.brakeHeld) return;
    this.brakeHeld = false;
    this.input.setBrake(false);
    this.brake?.classList.remove('is-held');
    this.brake?.setAttribute('aria-pressed', 'false');
  }

  // Space and Enter hold the pedal for keyboard players; the browser would
  // otherwise only fire a single click on keyup.
  #brakeKeyDown(event) {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    if (event.repeat) return;
    this.#brakeDown(event);
  }

  #brakeKeyUp(event) {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    this.#brakeUp();
  }

  #paintGear(gear) {
    for (const button of this.gearButtons) {
      const active = button.dataset.gear === gear;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
  }

  #paint() {
    if (!this.face) return;
    const degrees = (-this.steer * MAX_ANGLE * 180) / Math.PI;
    this.face.style.transform = `rotate(${degrees.toFixed(2)}deg)`;
    this.wheel?.setAttribute('aria-valuenow', String(Math.round(this.steer * 100)));
  }

  /** Angle of a pointer around the wheel's centre, in radians. */
  #pointerAngle(event) {
    const rect = this.wheel.getBoundingClientRect();
    const dx = event.clientX - (rect.left + rect.width / 2);
    const dy = event.clientY - (rect.top + rect.height / 2);
    return Math.atan2(dy, dx);
  }

  #shortestAngle(angle) {
    let value = angle;
    while (value > Math.PI) value -= Math.PI * 2;
    while (value < -Math.PI) value += Math.PI * 2;
    return value;
  }

  dispose() {
    this.#stopFrameLoop();
    if (this.wheel) {
      this.wheel.removeEventListener('pointerdown', this._onPointerDown);
      this.wheel.removeEventListener('pointermove', this._onPointerMove);
      this.wheel.removeEventListener('pointerup', this._onPointerUp);
      this.wheel.removeEventListener('pointercancel', this._onPointerUp);
      this.wheel.removeEventListener('lostpointercapture', this._onPointerUp);
    }
    if (this.brake) {
      this.brake.removeEventListener('pointerdown', this._onBrakeDown);
      this.brake.removeEventListener('pointerup', this._onBrakeUp);
      this.brake.removeEventListener('pointercancel', this._onBrakeUp);
      this.brake.removeEventListener('lostpointercapture', this._onBrakeUp);
      this.brake.removeEventListener('keydown', this._onBrakeKeyDown);
      this.brake.removeEventListener('keyup', this._onBrakeKeyUp);
      this.brake.removeEventListener('blur', this._onBrakeUp);
    }
  }
}

function clamp(value, min, max) {
  return value < min ? min : value > max ? max : value;
}
